/**
 * Takes a currency as either a float or a string and returns the value as a float.
 * It is forgiving as to the format of the argument. In particular $ and , are removed
 * before converting string to float.
 */
const parseCurrency = (amount) => {
    if (!amount) {
        return 0.0;
    }
    if (typeof amount === 'number') {
        return amount;
    }
    if (typeof amount === 'string') {
        return parseFloat(amount.replace(/,/g, '').replace('$', ''));
    }
    return parseFloat(amount);
};
/**
 * Validates and normalizes currency code to ISO 4217 format.
 * Expects a 3-letter uppercase currency code (e.g., 'USD', 'EUR', 'COP').
 * Falls back to 'USD' if invalid.
 */
const normalizeCurrencyCode = (currency) => {
    // Validate it's a 3-letter uppercase ISO currency code
    if (currency && /^[A-Z]{3}$/.test(currency)) {
        return currency;
    }
    // Default to USD if invalid or missing
    return 'USD';
};
/**
 * Formats a price for display with locale support.
 * Uses Intl.NumberFormat with currency formatting for proper locale-aware display.
 *
 * According to MDN: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
 * Using style: 'currency' ensures proper symbol placement and formatting per locale.
 *
 * Examples:
 * - es-ES with EUR: "1.234,56 €" (comma for decimals, period for thousands, symbol after)
 * - en-US with USD: "$1,234.56" (period for decimals, comma for thousands, symbol before)
 * - es-CO with COP: "1.234,56 $"
 *
 * @param amount - The price amount (number or string)
 * @param locale - The locale string (e.g., 'es-ES', 'en-US', 'es-co')
 * @param currency - The currency code (ISO 4217 like 'USD', 'EUR', 'COP'). Default: 'USD'
 * @returns Formatted price string with currency symbol in correct position for locale
 */
const formatPriceWithLocale = (amount, locale = 'en', currency = 'USD') => {
    if (!amount) {
        return '';
    }
    // Normalize by converting to a number
    const formattedAmount = parseCurrency(amount);
    // Normalize currency to ISO code
    const currencyCode = normalizeCurrencyCode(currency);
    try {
        // Use style: 'currency' for proper locale-aware currency formatting
        // This automatically handles symbol placement and formatting per locale
        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return formatter.format(formattedAmount);
    }
    catch (error) {
        // Fallback to default format if locale or currency is invalid
        console.warn(`[formatPriceWithLocale] Invalid locale "${locale}" or currency "${currencyCode}", using default format:`, error);
        return `${currencyCode} ${formattedAmount.toFixed(2)}`;
    }
};
export default formatPriceWithLocale;
//# sourceMappingURL=formatPriceWithLocale.js.map